import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { formatDateTime } from './utils/applicationDataMapper';
import { useApplication } from './hooks/useApplication';

const ApplicationHistory = ({ applicationData, mode, applicationId }) => {
  const { statuses, statusesLoading, statusesError, fetchApplicationStatuses } = useApplication(applicationId);
  const [sortConfig, setSortConfig] = React.useState({ key: 'date', direction: 'desc' });

  useEffect(() => {
    if (applicationId) {
      fetchApplicationStatuses();
    }
  }, [applicationId, fetchApplicationStatuses]);

  const columns = [
    { key: 'date', label: 'Дата', sortable: true, width: '15%' },
    { key: 'action', label: 'Дія', sortable: true, width: '20%' },
    { key: 'result', label: 'Результат', sortable: true, width: '15%' },
    { key: 'additionalInfo', label: 'Додаткова інформація', sortable: false, width: '35%' },
    { key: 'user', label: 'Користувач', sortable: true, width: '15%' }
  ];

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const getStatusText = (statusName) => {
    const statusMap = {
      'need-check': 'Заявку прийнято',
      'approved': 'Підтверджена - садочки обрані',
      'invite-sent': 'Надіслане запрошення',
      'invite-accepted': 'Запрошення прийняте',
      'invite-declined': 'Заява відхилена',
      'child-in-kindergarten': 'Дитина оформлена',
      'banned': 'Не обслуговується',
      'removed': 'Видалена'
    };
    return statusMap[statusName] || statusName;
  };

  const getHistoryResult = (statusName) => {
    const resultMap = {
      'need-check': 'В очікуванні',
      'approved': 'Успішно',
      'invite-sent': 'Інформація',
      'invite-accepted': 'Успішно',
      'declined': 'Відхилено',
      'child-in-kindergarten': 'Завершено',
      'banned': 'Відхилено',
      'removed': 'Відхилено'
    };
    return resultMap[statusName] || 'В очікуванні';
  };

  const getAdditionalInfo = (status) => {
    const info = [];
    
    if (status.kindergartenName) {
      info.push(`Дитячий садок: ${status.kindergartenName} (${status.kindergartenNumber})`);
    }
    
    if (status.kindergartenDistrict) {
      info.push(`Район: ${status.kindergartenDistrict}`);
    }
    
    if (status.groupNumber) {
      info.push(`Група: ${status.groupNumber}`);
    }
    
    if (status.action?.detail?.comment) {
      info.push(`Коментар: ${status.action.detail.comment}`);
    }
    
    return info.join('; ');
  };

  const getUserInfo = (action) => {
    if (!action) return 'Заявник';
    
    if (action.source === 'system') {
      return 'Система';
    }
    
    if (action.source === 'user' && action.detail) {
      return action.detail.name || 'Користувач';
    }
    
    return 'Заявник';
  };

  const sortedData = [...(statuses || [])].sort((a, b) => {
    if (sortConfig.key === 'date') {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      if (dateA < dateB) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (dateA > dateB) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    }
    
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const getResultBadge = (result) => {
    const resultConfig = {
      'Успішно': 'status-success',
      'В очікуванні': 'status-pending',
      'Інформація': 'status-info',
      'Завершено': 'status-success',
      'Відхилено': 'status-error'
    };
    
    const className = resultConfig[result] || 'status-default';
    return <span className={`status-badge ${className}`}>{result}</span>;
  };

  if (statusesLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Завантаження...</span>
        </div>
        <p className="mt-2">Завантаження історії...</p>
      </div>
    );
  }

  if (statusesError) {
    return (
      <div className="alert alert-danger m-3">
        <strong>Помилка завантаження історії:</strong> {statusesError}
        <div className="mt-2">
          <button className="btn btn-sm btn-outline-danger" onClick={fetchApplicationStatuses}>
            Спробувати знову
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="application-history">
      <div className="table-responsive">
        <table className="history-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th 
                  key={column.key}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.key)}
                  className={column.sortable ? 'sortable' : ''}
                >
                  <div className="d-flex align-items-center">
                    {column.label}
                    {column.sortable && (
                      <FontAwesomeIcon 
                        icon={getSortIcon(column.key)} 
                        className="ms-1" 
                        style={{ fontSize: '12px' }}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => (
              <tr key={item._id || `history-${index}`}>
                <td>{formatDateTime(item.date)}</td>
                <td>{getStatusText(item.name)}</td>
                <td>{getResultBadge(getHistoryResult(item.name))}</td>
                <td>{getAdditionalInfo(item)}</td>
                <td>{getUserInfo(item.action)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedData.length === 0 && (
        <div className="no-data">
          Історія відсутня
        </div>
      )}
    </div>
  );
};

export default ApplicationHistory;